import type { BaseSyntheticEvent, HTMLAttributes, ReactNode } from "react";

import { useUser } from "@auth0/nextjs-auth0";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import clsx from "clsx";

import { robotoCondensed } from "@/fonts";
import { LoginButton } from "@/src/components/navigation/LoginButton";
import { LogoutButton } from "@/src/components/navigation/LogoutButton";
import { NavigationItemsConsumer } from "@/src/components/navigation/NavigationItemsConsumer";
import { NavigationItemsMember } from "@/src/components/navigation/NavigationItemsMember";
import { Button } from "@/src/components/ui/Button/Button";

export interface NavigationItem {
  href: string;
  icon?: string;
  label: string;
}

export interface NavigationItemsProps extends HTMLAttributes<HTMLDivElement> {
  readonly consumerItems: NavigationItem[];
  readonly memberItems: NavigationItem[];
  readonly opened: boolean;
}

export function NavigationItems({
  consumerItems,
  memberItems,
  opened,
}: NavigationItemsProps): ReactNode {
  const { user } = useUser();

  return (
    <>
      <NavigationItemsConsumer
        className={clsx("hidden", "md:flex")}
        items={consumerItems}
      />

      <NavigationMenu.Item className={clsx("list-none", "md:hidden")}>
        <NavigationMenu.Trigger
          asChild
          onPointerLeave={preventDefault}
          onPointerMove={preventDefault}
        >
          <button
            aria-label={opened ? "Fermer le menu" : "Ouvrir le menu"}
            className={clsx(
              "group flex cursor-pointer items-center self-stretch overflow-hidden rounded-bl px-3 py-2.5",
              "focus-visible:outline-hidden",
              "hover:bg-primary-hover",
            )}
          >
            {opened ? (
              <XMarkIcon
                className={clsx(
                  "size-9 opacity-100 transition duration-200 ease-in-out",
                  { "opacity-0": !opened },
                )}
                strokeWidth={2}
              />
            ) : (
              <Bars3Icon
                className={clsx(
                  "size-9 opacity-100 transition duration-200 ease-in-out",
                  { "opacity-0": opened },
                )}
                strokeWidth={2}
              />
            )}
          </button>
        </NavigationMenu.Trigger>
        <NavigationMenu.Content
          className="bg-primary absolute right-0 top-14 flex w-screen flex-col items-center gap-5 p-8 text-white shadow-2xl transition duration-200 ease-out"
          data-testid="burger-menu-small-screen"
          onPointerEnter={preventDefault}
          onPointerLeave={preventDefault}
        >
          <NavigationItemsConsumer items={consumerItems} />
          {user ? (
            <>
              <div className="w-full max-w-80 rounded-sm bg-white p-6">
                <h2
                  className={clsx(
                    "text-primary mb-4 text-xl tracking-wide",
                    robotoCondensed.className,
                  )}
                >
                  Espace sociétaire
                </h2>
                <NavigationItemsMember items={memberItems} />
              </div>
              <LogoutButton />
            </>
          ) : (
            <LoginButton className="mb-2" />
          )}
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      {user ? (
        <NavigationMenu.Item className={clsx("relative hidden", "md:block")}>
          <NavigationMenu.Trigger
            asChild
            onPointerLeave={preventDefault}
            onPointerMove={preventDefault}
          >
            <div>
              <Button
                aria-haspopup="true"
                className={clsx("mx-3 w-52", "md:block", "lg:w-64")}
                darkBackground
                size="medium"
              >
                Espace sociétaire
              </Button>
            </div>
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className="bg-primary absolute right-0 w-80 rounded-sm p-5 text-white shadow-xl transition duration-200 ease-out"
            data-testid="member-menu-large-screen"
            onPointerEnter={preventDefault}
            onPointerLeave={preventDefault}
          >
            <NavigationItemsMember
              className="mb-5 rounded-sm bg-white p-6"
              items={memberItems}
            />
            <LogoutButton />
          </NavigationMenu.Content>
        </NavigationMenu.Item>
      ) : (
        <LoginButton
          className={clsx("mx-3 hidden w-52", "md:block", "lg:w-64")}
        />
      )}
    </>
  );
}

function preventDefault(event: BaseSyntheticEvent): void {
  event.preventDefault();
}
